import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {JWT_OPTIONS, JwtModule} from '@auth0/angular-jwt';
import {
    AlertListModule,
    AlertListService,
    ApiModule,
    ErrorLogService,
    HttpLoaderFactory,
    I18nHelperModule,
    LOGGING_ERROR_HANDLER_PROVIDERS,
    MissingI18nHandler,
    MissingTranslationHandler,
    QueryStringService,
    SliderSwitchModule,
    TranslateLoader,
    TranslateModule,
    TranslationSelectorModule,
    TranslationSelectorService,
} from '@xref/ngx';
import {Angulartics2Module} from 'angulartics2';
import * as Cookies from 'js-cookie';
import {AlertModule} from 'ngx-bootstrap/alert';
import {TooltipModule} from 'ngx-bootstrap/tooltip';

import {environment} from '../environments/environment';
import {AppComponent} from './app.component';
import {AuthService} from './services/auth/auth.service';
import {ElementHelperService} from './services/element-helper/element-helper.service';
import {StylingCheckService} from './services/styling-check/styling-check.service';
import {EmployerDetailsService} from './services/employer-details/employer-details.service';
import {EmployerOverviewService} from './services/employer-overview/employer-overview.service';
import {IdService} from './services/id/id.service';
import {HttpInterceptorService} from './services/interceptor/http-interceptor.service';
import {MarketingService} from './services/marketing/marketing.service';
import {OauthService} from './services/oauth/oauth.service';
import {TokenService} from './services/token/token.service';
import {ConstantsService} from './services/constants/constants.service';
import {AppRoutingModule} from './shared/router';
import {SharedModule} from './shared/shared.module';
import {OauthQuestionnaireService} from './services/oauth-questionnaire/oauth-questionnaire.service';
import {ModalModule} from 'ngx-bootstrap/modal';
import {XrefLibFeatureFlagService, ButtonModule, BannerModule} from '@xref/xref-lib';
import {PermissionsService} from "./services/permissions/permissions.service";
import { SignupVerifyModule } from './modules/signup-verify/signup-verify.module';
import { JoinTeamComponent } from './modules/join-team/join-team.component';
import {OmniLoaderModule} from "./components/omni-loader/omni-loader.module";
import {FooterModule} from "./components/footer/footer.module";
import {HeaderModule} from "./components/header/header.module";
import {UserDetailsModule} from "./components/user-details/user-details.module";
import {UserVerifyModule} from "./components/user-verify/user-verify.module";
import {UserLoggedInModule} from "./components/user-logged-in/user-logged-in.module";
import {FormsModule} from "@angular/forms";
import {OrgCreationModule} from "./components/org-creation/org-creation.module";

const apiConfig = {
    url: environment.API
};

const i18nHelperConfig = {
    LOCALE: '',
    LOCALE_TOKEN: ConstantsService.LOCALE_TOKEN,
    SUPPORTED_LOCALES: ConstantsService.SUPPORTED_LOCALES,
    storageType: 'cookie',
    localeStorageKey: ConstantsService.LOCALE_TOKEN,
    DOMAIN: environment.DOMAIN
};

export function jwtOptionsFactory() {
    return {
        tokenName: ConstantsService.AUTH_TOKEN,
        headerName: 'X-JWT',
        authScheme: 'JWT ',
        tokenGetter: (() => {
            // Can't use permissionService due to circular dependency
            const token = sessionStorage.getItem(ConstantsService.AUTH_TOKEN)
            || Cookies.get(ConstantsService.AUTH_TOKEN);
            return token;
        }),
        globalHeaders: [
            {'Content-Type': 'application/json'},
            {'Accept': 'application/json'}
        ],
        noClientCheck: true,
        allowedDomains: ConstantsService.AUTH_DOMAIN_WHITELIST
    };
}

export function permissionsFactory(provider: PermissionsService) {
    return () => provider.initService();
}

@NgModule({ declarations: [
    AppComponent,
    JoinTeamComponent
],
bootstrap: [AppComponent], imports: [BrowserModule,
    FooterModule,
    HeaderModule,
    UserDetailsModule,
    UserVerifyModule,
    OrgCreationModule,
    FormsModule,
    UserLoggedInModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    JwtModule.forRoot({
        jwtOptionsProvider: {
            provide: JWT_OPTIONS,
            useFactory: jwtOptionsFactory
        }
    }),
    ApiModule.forRoot(apiConfig),
    AlertListModule.forRoot(),
    AlertModule.forRoot(),
    TooltipModule.forRoot(),
    TranslateModule.forRoot({
        missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MissingI18nHandler },
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient]
        }
    }),
    I18nHelperModule.forRoot(i18nHelperConfig),
    Angulartics2Module.forRoot(),
    TranslationSelectorModule,
    SliderSwitchModule,
    ModalModule.forRoot(),
    ButtonModule,
    SignupVerifyModule,
    OmniLoaderModule,
    BannerModule], providers: [
    provideAppInitializer(() => {
        const initializerFn = (permissionsFactory)(inject(PermissionsService));
        return initializerFn();
    }),
    XrefLibFeatureFlagService,
    AlertListService,
    AuthService,
    MarketingService,
    LOGGING_ERROR_HANDLER_PROVIDERS,
    ErrorLogService,
    TranslationSelectorService,
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    EmployerDetailsService,
    StylingCheckService,
    IdService,
    ElementHelperService,
    OauthService,
    EmployerOverviewService,
    QueryStringService,
    TokenService,
    OauthQuestionnaireService,
    provideHttpClient(withInterceptorsFromDi()),
] })
export class AppModule {}
